/**
 * Descuentos de planes (público) + cálculo de precio efectivo.
 *
 * Modelo de precios por MULTIPLICADOR sobre price_monthly (ESPEJO EXACTO del
 * backend TB\Billing\Discounts):
 *   MESES  = { monthly:1, semiannual:6, yearly:12 }   (duración del servicio)
 *   FACTOR = { monthly:1, semiannual:5, yearly:10 }   (meses que se PAGAN)
 *   base (lista/tachado) = price_monthly × MESES[cycle]
 *   final (cobrado)      = round(price_monthly × FACTOR[cycle] × promoFactor)
 *   "meses gratis" = MESES − FACTOR = {0,1,2}.
 *
 * `annual_percent` quedó DEPRECADO (el ahorro sale del ciclo). Se conserva en el
 * shape sólo por compat. La PROMO de lanzamiento (on/off + %) se aplica ENCIMA en
 * los 3 ciclos: promoFactor = 1 − (promo.active ? promo.percent : 0)/100.
 *
 * `effectivePrice(...).final` DEBE ser idéntico a Billing\Discounts::effectivePrice
 * del backend (mismo round a entero PYG) para que lo mostrado == lo cobrado.
 */
import { useQuery } from '@tanstack/react-query';

import { api } from './api';

/** Shape público de `GET /pricing/discounts`. */
export interface Discounts {
  annual_percent: number;
  promo: {
    active: boolean;
    percent: number;
    ends_at: string;
    label: string;
  };
}

export type BillingCycle = 'monthly' | 'semiannual' | 'yearly';

/** Duración real del servicio en meses por ciclo. */
export const CYCLE_MONTHS: Record<BillingCycle, number> = {
  monthly: 1,
  semiannual: 6,
  yearly: 12,
};

/** Meses que se PAGAN por ciclo (multiplicador sobre price_monthly). */
export const CYCLE_FACTOR: Record<BillingCycle, number> = {
  monthly: 1,
  semiannual: 5,
  yearly: 10,
};

/** Resultado del cálculo de precio efectivo para un plan/ciclo. */
export interface EffectivePrice {
  /** Precio de lista (sin descuento) del período, en ₲ = price_monthly × MESES. */
  base: number;
  /** Precio final del período (con descuento aplicado), en ₲. Igual a periodTotal. */
  final: number;
  /** Total a pagar por el período completo del ciclo (alias de final), en ₲. */
  periodTotal: number;
  /** Equivalente mensual del final (final / MESES), en ₲ — para el número grande "/mes". */
  monthlyEquiv: number;
  /** Meses gratis del ciclo = MESES − FACTOR ({0,1,2}). */
  freeMonths: number;
  /** % de descuento efectivo aplicado (entero 0..100). */
  pct: number;
  /** true si hubo descuento (final < base). */
  discounted: boolean;
}

const DISCOUNTS_KEY = ['pricing', 'discounts'] as const;

/**
 * Descuentos públicos. Tolera error → undefined (sin descuento). No entra en el
 * ciclo de auth (skipAuthRefresh): es un endpoint público.
 */
export function usePricingDiscounts() {
  return useQuery({
    queryKey: DISCOUNTS_KEY,
    queryFn: () => api.get<Discounts>('/pricing/discounts', { skipAuthRefresh: true }),
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/**
 * Factor multiplicador del precio (0..1) — SÓLO la promo de lanzamiento. El
 * ahorro por ciclo (pagar menos meses) lo aporta CYCLE_FACTOR. Sin `d` (no cargó)
 * → 1 (sin promo). Debe ser IDÉNTICO a TB\Billing\Discounts::effectiveFactor.
 */
function promoFactor(d: Discounts | undefined): number {
  const promoPct = d?.promo.active ? d.promo.percent : 0;
  return 1 - promoPct / 100;
}

/**
 * Precio efectivo del período a partir del precio mensual base.
 *
 * base         = priceMonthly × MESES[cycle]                (lista / tachado)
 * final/period = round(priceMonthly × FACTOR[cycle] × promo) (cobrado, entero PYG)
 * monthlyEquiv = round(final / MESES[cycle])                (número grande "/mes")
 * freeMonths   = MESES − FACTOR                             ({0,1,2})
 *
 * El descuento del ciclo aplica aunque `d` sea undefined (es intrínseco al ciclo);
 * la promo sólo si `d` la trae activa.
 */
export function effectivePrice(
  priceMonthly: number,
  cycle: BillingCycle,
  d: Discounts | undefined,
): EffectivePrice {
  const months = CYCLE_MONTHS[cycle];
  const factor = CYCLE_FACTOR[cycle];
  const base = priceMonthly * months;
  // Usa el factor de promo (no el % redondeado) para evitar drift — igual que el backend.
  const final = Math.round(priceMonthly * factor * promoFactor(d));
  const monthlyEquiv = Math.round(final / months);
  const freeMonths = months - factor;
  const pct = base > 0 ? Math.round((1 - final / base) * 100) : 0;
  return { base, final, periodTotal: final, monthlyEquiv, freeMonths, pct, discounted: final < base };
}
